import { FormFieldComponent, InputState } from './FormField.types';
import styled from 'styled-components';
import { Flexbox, Typography, Icon, VisuallyHidden } from "@hiiretail/synergy-ui";
import { isNil } from "common/components/TextArea/utils/isNil";

// TODO: Use theme colors
const InputGroupWrapper = styled(Flexbox)<{ fullWidth: boolean }>`
  width: ${(props) => (props.fullWidth ? '100%' : 'max-content')};
  * {
    font-family: 'Inter', sans-serif;
  }
`;

const getFeedbackData = (inputState: InputState) => {
  if (inputState.error) {
    return { color: '#FF1F3A', text: inputState.error };
  }

  if (inputState.warning) {
    return { color: '#FF6B00', text: inputState.warning };
  }

  if (inputState.success) {
    return { color: '#00D27E', text: inputState.success };
  }

  return null;
};

const InputWrapper = styled(Flexbox)<{
  disabled: boolean;
  state: InputState;
  fullWidth: boolean;
}>`
  padding: ${({ theme }) => `${theme.spacing(2)}px ${theme.spacing(3)}px`};
  border-radius: 4px;
  transition: border 0.1s ease-in-out;
  background-color: ${({ disabled }) => (disabled ? '#FAFAFA' : '#FFF')};
  ${({ fullWidth }) => (fullWidth ? 'width: 100%;' : '')}
  border: 1px solid
    ${({ state }) => getFeedbackData(state)?.color || '#cecece'};
  &:hover {
    //  TODO: Discuss usage of darker color
    border-color: ${({ state }) => getFeedbackData(state)?.color || '#cecece'};
  }
  &:focus-within {
    border-color: ${({ state }) => getFeedbackData(state)?.color || '#8700ff'};
  }
`;

const Hint = styled(Typography)`
  margin-top: ${({ theme }) => theme.spacing(2)}px;
  color: #616161;
`;

const FeedbackMessageWrapper = styled(Flexbox)`
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing(4)}px;
`;

const FeedbackMessageIconWrapper = styled(Flexbox)`
  height: 16px;
  width: 16px;
  margin-right: ${({ theme }) => theme.spacing(1)}px;
`;

const FeedbackMessage = (inputState: InputState) => {
  // It can't be null in here as we check if there's an input state before mounting the FeedbackMessage
  const feedbackData = getFeedbackData(inputState) as NonNullable<
    ReturnType<typeof getFeedbackData>
    >;
  return (
    <FeedbackMessageWrapper container alignItems="center" justifyContent="flex-start">
      <FeedbackMessageIconWrapper container alignItems="center" justifyContent="center">
        <Icon.WarningRound color={() => feedbackData.color} size="sm" />
      </FeedbackMessageIconWrapper>
      <Typography variant="caption">{feedbackData.text}</Typography>
    </FeedbackMessageWrapper>
  );
};

const StyledTypographyLabel = styled(Typography)<{
  required: boolean;
  hideLabel: boolean;
  htmlFor: string;
}>`
  ${({ theme, hideLabel }) => (hideLabel ? '' : `margin-bottom: ${theme.spacing(2)}px`)};
  ${({ required }) =>
  required
  ? `
        &:after {
          bottom: 2px;
          content: '*';
          color: #ff1f3a;
          font-style: normal;
          font-weight: 600;
          font-size: 12px;
          position: relative;
        }
      `
  : ''}
`;

const Prefix = styled(Typography)`
  margin-right: ${(props) => props.theme.spacing(3)}px;
`;

const Suffix = styled(Typography)`
  margin-left: ${(props) => props.theme.spacing(3)}px;
`;

const TailIconWrapper = styled(Flexbox)<{ disabled: boolean }>`
  margin-left: ${({ theme }) => theme.spacing(2)}px;
  button {
    height: 24px;
    width: 24px;
    padding: 0;
  }
`;

const LeadingIconWrapper = styled(Flexbox)<{ disabled: boolean }>`
  margin-right: ${({ theme }) => theme.spacing(2)}px;
`;

export const FormField: FormFieldComponent = ({
                                                children,
                                                prefix,
                                                suffix,
                                                leadingIcon,
                                                trailingIcon,
                                                error,
                                                warning,
                                                success,
                                                hint,
                                                label,
                                                disabled = false,
                                                required = false,
                                                inputId,
                                                fullWidth = false,
                                                hideLabel = false,
                                                className,
                                              }) => {
  const color = disabled ? '#9E9E9E' : '#212121';
  const hasState = [success, error, warning].some((state) => !isNil(state));
  return (
    <InputGroupWrapper container direction="column" alignItems="flex-start" fullWidth={fullWidth}>
      <StyledTypographyLabel
        weight="semibold"
        forwardedAs="label"
        htmlFor={inputId}
        required={required}
        color={() => color}
        variant="body2"
        hideLabel={hideLabel}>
        {hideLabel ? <VisuallyHidden>{label}</VisuallyHidden> : label}
      </StyledTypographyLabel>
      <InputWrapper
        container
        alignItems="center"
        disabled={disabled}
        state={{ error, warning, success }}
        className={className}
        fullWidth>
        {leadingIcon ? (
          <LeadingIconWrapper container item alignItems="center" disabled={disabled}>
            {leadingIcon}
          </LeadingIconWrapper>
        ) : null}
        {prefix ? (
          <Prefix forwardedAs="div" variant="body2" color={() => color}>
            {prefix}
          </Prefix>
        ) : null}
        {children}
        {suffix ? (
          <Suffix forwardedAs="div" variant="body2" color={() => color}>
            {suffix}
          </Suffix>
        ) : null}
        {trailingIcon ? (
          <TailIconWrapper container alignItems="center" disabled={disabled}>
            {trailingIcon}
          </TailIconWrapper>
        ) : null}
      </InputWrapper>
      {hasState ? <FeedbackMessage success={success} error={error} warning={warning} /> : null}
      {hint ? <Hint variant="caption">{hint}</Hint> : null}
    </InputGroupWrapper>
  );
};
